import { useState } from "react";
import { useTranslation } from "react-i18next";
import OperatorLayout from "./OperatorLayout";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AvatarPicker from "@/components/operator/AvatarPicker";

const OperatorProfile = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [displayName, setDisplayName] = useState(user?.user_metadata?.display_name || "");
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.user_metadata?.avatar_url || null);

  const handlePasswordChange = async () => {
    if (newPassword.length < 6) {
      toast.error(t("op.profilePasswordMinLength"));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("op.profilePasswordMismatch"));
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success(t("op.profilePasswordSuccess"));
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast.error(err.message || t("op.profilePasswordError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateName = async () => {
    setIsUpdatingName(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { display_name: displayName },
      });
      if (error) throw error;
      toast.success(t("op.profileNameSuccess"));
    } catch (err: any) {
      toast.error(err.message || t("op.profileNameError"));
    } finally {
      setIsUpdatingName(false);
    }
  };

  return (
    <OperatorLayout title={t("op.profileTitle")} subtitle={t("op.profileSubtitle")}>
      <div className="max-w-2xl space-y-8">
        {/* Avatar & Info */}
        <section className="p-6 border border-border bg-card space-y-6">
          <div className="flex items-center gap-6">
            <AvatarPicker
              userId={user?.id || ""}
              currentAvatarUrl={avatarUrl}
              displayName={displayName || user?.email || "OP"}
              onAvatarChange={(url) => setAvatarUrl(url)}
            />
            <div>
              <p className="text-body font-medium">{user?.email}</p>
              <p className="text-caption">{t("op.profileRole")}</p>
            </div>
          </div>

          <div className="divider-line" />

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-micro block">{t("op.profileDisplayName")}</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full h-11 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary transition-colors"
                placeholder={t("op.profileNamePlaceholder")}
              />
            </div>
            <button
              onClick={handleUpdateName}
              disabled={isUpdatingName}
              className="h-10 px-6 bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isUpdatingName ? t("common.loading") : t("op.profileSaveName")}
            </button>
          </div>
        </section>

        {/* Password Change */}
        <section className="p-6 border border-border bg-card space-y-6">
          <h2 className="text-subheadline">{t("op.profileChangePassword")}</h2>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-micro block">{t("op.profileNewPassword")}</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t("op.profilePasswordPlaceholder")}
                className="w-full h-11 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <div className="space-y-2">
              <label className="text-micro block">{t("op.profileConfirmPassword")}</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t("op.profileConfirmPlaceholder")}
                className="w-full h-11 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <button
              onClick={handlePasswordChange}
              disabled={isSaving || newPassword.length < 6 || newPassword !== confirmPassword}
              className="h-10 px-6 bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isSaving ? t("common.loading") : t("op.profileChangePassword")}
            </button>
          </div>
        </section>

        {/* Account Info */}
        <section className="p-6 border border-border bg-card space-y-4">
          <h2 className="text-subheadline">{t("op.profileAccountDetails")}</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-micro block mb-1">{t("op.profileEmail")}</span>
              <p className="text-body">{user?.email}</p>
            </div>
            <div>
              <span className="text-micro block mb-1">{t("op.profileCreatedAt")}</span>
              <p className="text-body">
                {user?.created_at
                  ? new Date(user.created_at).toLocaleDateString("de-DE")
                  : "—"}
              </p>
            </div>
            <div>
              <span className="text-micro block mb-1">{t("op.profileLastLogin")}</span>
              <p className="text-body">
                {user?.last_sign_in_at
                  ? new Date(user.last_sign_in_at).toLocaleDateString("de-DE", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "—"}
              </p>
            </div>
          </div>
        </section>
      </div>
    </OperatorLayout>
  );
};

export default OperatorProfile;